import React, {
  type PropsWithChildren,
  useCallback, useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  ActiveBoundaryFillInfoComponent, type ActiveBoundaryFillInfoEntities,
} from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/activeBoundaryFillInfo.component';
import { extraBoundaryFillActivated } from '~/sidebar/sidebarApps/mapTools/boundary/extraFills/boundaryExtraFills.actionCreators';
import { useActiveFillIdSelector } from '~/sidebar/sidebarApps/mapTools/boundary/extraFills/boundaryExtraFillSelectors';
import {
  type BoundaryFillConfig,
  isBoundaryFillGroupConfig, isBoundaryFillNumericConfig,
} from '~/sidebar/sidebarApps/mapTools/boundary/extraFills/extraFills.types';
import { convertBoundaryFillSettingsToBTGSettingsOverride } from '~/sidebar/sidebarApps/mapTools/boundary/extraFills/extraFills.utils';
import { getExtraActionsForExtraFill } from '~/sidebar/sidebarApps/mapTools/boundary/extraFills/fillTitleExtraActions';
import { useGetBoundaryFillEntities } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useGetBoundaryFillEntities';
import { type BoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';

type ActiveBoundaryExtraFillContainerProps = Readonly<{
  boundaryTerritoryGroup: BoundaryTerritoryGroup;
  fillInfo: BoundaryFillConfig;
}>;

export const ActiveBoundaryExtraFillContainer = ({
  boundaryTerritoryGroup,
  fillInfo,
  children,
}: PropsWithChildren<ActiveBoundaryExtraFillContainerProps>) => {
  const dispatch = useDispatch();
  const activeFillId = useActiveFillIdSelector(boundaryTerritoryGroup.boundaryTerritoryGroupId);

  const patchedBTG: BoundaryTerritoryGroup = {
    ...boundaryTerritoryGroup,
    settings: {
      ...boundaryTerritoryGroup.settings,
      ...convertBoundaryFillSettingsToBTGSettingsOverride(fillInfo),
    },
  };

  const entities: ActiveBoundaryFillInfoEntities | null = useGetBoundaryFillEntities({
    boundaryTerritoryGroup: patchedBTG,
    columnId: (isBoundaryFillNumericConfig(fillInfo) || isBoundaryFillGroupConfig(fillInfo)) ?
      fillInfo.boundaryFillData.columnId :
      undefined,
    calculateBucketFunction: isBoundaryFillNumericConfig(fillInfo) ?
      fillInfo.numericCalculateBucketFunction :
      undefined,
    boundaryTerritoryType: fillInfo.boundaryTerritoryType,
  });

  const extraActions = useMemo(() => getExtraActionsForExtraFill({
    boundaryTerritoryGroupId: boundaryTerritoryGroup.boundaryTerritoryGroupId,
    extraFillId: fillInfo.boundaryFillId,
  }), [boundaryTerritoryGroup.boundaryTerritoryGroupId, fillInfo.boundaryFillId]);

  const activateFill = useCallback(() => {
    dispatch(extraBoundaryFillActivated(
      {
        extraBoundaryFillId: fillInfo.boundaryFillId,
        boundaryTerritoryGroupId: boundaryTerritoryGroup.boundaryTerritoryGroupId,
      },
    ));
  }, [boundaryTerritoryGroup.boundaryTerritoryGroupId, dispatch, fillInfo.boundaryFillId]);

  if (!entities) {
    return null;
  }

  return (
    <React.Fragment key={fillInfo.boundaryFillId}>
      <ActiveBoundaryFillInfoComponent
        fillEntities={entities}
        extraActions={extraActions}
        onClick={activateFill}
      />
      {activeFillId === fillInfo.boundaryFillId ? (
        children
      ) : null}
    </React.Fragment>
  );
};
